import React, { useState, useRef, forwardRef } from 'react';
import { Tag, Input, Typography, Tooltip } from 'antd';
import classnames from 'classnames';

import './EmailTagInput.less';

const { Text } = Typography;

// ── Constants ─────────────────────────────────────────────────────────────────

const PASTE_SEPARATORS = /[;,\n]/;
const DEFAULT_SEPARATOR = ';';
const DEFAULT_PLACEHOLDER = 'Input email and press Enter or ;';

// ── Helpers ───────────────────────────────────────────────────────────────────

const splitTags = (value, separator) =>
  value
    ? value
        .split(separator)
        .map((e) => e.trim())
        .filter(Boolean)
    : [];

const isPastedInput = (val) => PASTE_SEPARATORS.test(val);

const runValidators = async (value, validators) => {
  if (!validators?.length) return [];
  const results = await Promise.all(
    validators.map((validator) => Promise.resolve(validator(value)))
  );
  return results.filter((r) => r.result !== true).map((r) => r.message);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const EmailTag = ({ tag, disabled, onRemove }) => (
  <Tooltip title={tag}>
    <Tag
      closable={!disabled}
      onClose={(e) => {
        e.preventDefault();
        onRemove(tag);
      }}
      style={{ marginBottom: 2, marginRight: 4 }}
    >
      <span className='email-tag-input__tag-text'>{tag}</span>
    </Tag>
  </Tooltip>
);

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Outlook-style email tag input.
 * - Input is always visible at the end of chips (no button click needed)
 * - Press Enter or `;` to confirm the typed email as a chip
 * - Backspace on empty input removes the last chip
 * - Blur auto-confirms any pending text
 * - Paste support: splits on `;` `,` or newline and adds all valid emails at once
 * - Validates via `regexValidators` before adding a chip
 */
const EmailTagInput = forwardRef((props, ref) => {
  const {
    value: valueProps,
    onChange,
    regexValidators,
    disabled,
    className,
    separator = DEFAULT_SEPARATOR,
    placeholder = DEFAULT_PLACEHOLDER,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const tags = splitTags(valueProps, separator);

  // ── Tag mutation helpers ──────────────────────────────────────────────────

  const emitChange = (newTags) => onChange?.(newTags.join(separator));

  const removeLast = () => emitChange(tags.slice(0, -1));

  const removeTag = (tag) => emitChange(tags.filter((t) => t !== tag));

  // ── Single-tag commit (typed) ─────────────────────────────────────────────

  const commitTag = async (raw) => {
    const value = raw.trim();
    if (!value) return;

    if (tags.includes(value)) {
      setError('Email already exists');
      return;
    }

    const validationErrors = await runValidators(value, regexValidators);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setInputValue('');
    setError('');
    emitChange([...tags, value]);
  };

  // ── Batch-tag commit (pasted) ─────────────────────────────────────────────

  const commitPastedTags = async (val) => {
    const parts = val
      .split(PASTE_SEPARATORS)
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length === 1) {
      // Single segment typed with a trailing separator (e.g. "email@x.com;")
      commitTag(parts[0]);
      return;
    }

    const accumulated = [...tags];
    const batchErrors = [];

    for (const part of parts) {
      if (accumulated.includes(part)) continue;

      const validationErrors = await runValidators(part, regexValidators);
      if (validationErrors.length > 0) {
        batchErrors.push(`${part}: ${validationErrors.join(', ')}`);
        continue;
      }

      accumulated.push(part);
    }

    setInputValue('');
    setError(batchErrors.length > 0 ? batchErrors.join(' | ') : '');
    emitChange(accumulated);
  };

  // ── Input event handlers ──────────────────────────────────────────────────

  const handleInputChange = async (e) => {
    const val = e.target.value;

    if (isPastedInput(val)) {
      await commitPastedTags(val);
      return;
    }

    setInputValue(val);
    if (error) setError('');
  };

  const handlePressEnter = (e) => {
    e.preventDefault();
    commitTag(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeLast();
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) commitTag(inputValue);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={classnames('email-tag-input', className, {
        'email-tag-input--disabled': disabled,
        'email-tag-input--error': !!error,
      })}
      onClick={() => !disabled && inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <EmailTag
          key={tag}
          tag={tag}
          disabled={disabled}
          onRemove={removeTag}
        />
      ))}

      {!disabled && (
        <Input
          ref={inputRef}
          bordered={false}
          size='small'
          className='email-tag-input__input'
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPressEnter={handlePressEnter}
          onBlur={handleBlur}
          placeholder={tags.length === 0 ? placeholder : ''}
          disabled={disabled}
        />
      )}

      {error && (
        <Text type='danger' className='email-tag-input__error'>
          {error}
        </Text>
      )}
    </div>
  );
});

EmailTagInput.displayName = 'EmailTagInput';

export default EmailTagInput;
